// =====================================================================
// ClientPortail.jsx — Commandes prêtes v2 (16-10-2026) — page PUBLIQUE /client/<token>
// =====================================================================
// Le client (ex. Nespart sales) ouvre ici, SANS compte, la liste de ses commandes
// (SPEC-v2 §1.7, §3 « Page client publique », §5) : 90 derniers jours, plus
// récentes d'abord, jamais les commandes de test. Filtres : toutes / encore à quai
// (tout ou partie) / chargées ; détail dépliable (unités, SSCC, cm, kg, chargée ou
// non) ; lien vers les photos (page /order/<token> du dernier mail).
// Bilingue : anglais + français (bouton de langue ; départ = langue du navigateur).
// Le jeton du lien EST l'accès (révocable / régénérable dans Paramètres clients) ;
// toute erreur de jeton donne le même écran, dans les deux langues. Plein écran,
// styles autonomes, jamais indexée — comme OrderPhotos.jsx.
// Logique PURE : pages/commande-prete/portail.js (testée).
// =====================================================================

import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { publicClientPortal } from '../services/api.js';
import { abregeSscc } from './commande-prete/sscc.js';
import {
  TEXTES, FILTRES, langueDepart, etatCommande, filtrerCommandes, compteurs, nomDetail, texteUnitesCommande, totaux,
  resteNombre, texteDims, textePoids, lienSur, dateHeure, uniteChargee, dateChargeUnite,
} from './commande-prete/portail.js';

const wrap = { maxWidth: 1080, margin: '0 auto', padding: '20px 16px 48px', fontFamily: 'system-ui, -apple-system, "Segoe UI", Arial, sans-serif', color: '#17232a', boxSizing: 'border-box' };
const brand = { fontWeight: 800, color: '#0f6e74', fontSize: 15, letterSpacing: '.01em' };
// Mise à jour automatique (29-10-2026, demande Silviu : « la page doit s'actualiser seule ») : toutes les 2 min tant que
// la page est VISIBLE, et au retour sur l'onglet si la lecture précédente (réussie ou non) a 2 min — au plus ~5 lectures
// par 10 min et par onglet ; le serveur en accepte 60 par 10 min et par adresse IP (tous les onglets d'un bureau ensemble).
const AUTO_MS = 2 * 60_000;

function langueInitiale() {
  try { return langueDepart(navigator.language); } catch { return 'en'; }
}

export default function ClientPortail() {
  const { token } = useParams();
  const [langue, setLangue] = useState(langueInitiale);
  const [data, setData] = useState(null);
  const [etat, setEtat] = useState('loading');   // loading | ok | invalid | trop | erreur
  const [filtre, setFiltre] = useState('toutes');
  const [ouvertes, setOuvertes] = useState(() => new Set());
  const [lu, setLu] = useState(null);           // heure de la dernière lecture
  const [rafraichit, setRafraichit] = useState(false);
  const [echecActu, setEchecActu] = useState(null);   // actualisation ratée : la liste affichée reste
  const [autoEnPause, setAutoEnPause] = useState(false);   // trop de demandes : la lecture automatique attend (dit en tête)
  const affichee = useRef(false);                     // une liste est à l'écran
  const derniereLecture = useRef(0);                  // heure (ms) de la dernière lecture partie (réussie ou non)
  const derniereReussite = useRef(0);                 // départ (ms) de la dernière lecture réussie
  const pauseJusqua = useRef(0);                      // trop de demandes : pas de lecture automatique avant (ms)
  const enVol = useRef(0);                            // lectures en route : une automatique attend la fin d'un ↻
  const seqLecture = useRef(0);                       // seule la DERNIÈRE lecture partie s'affiche
  const jetonCourant = useRef(token);                 // une réponse pour un AUTRE lien est ignorée
  jetonCourant.current = token;
  const T = TEXTES[langue];

  const lire = useCallback(async (actif = () => true, { auto = false } = {}) => {
    const n = ++seqLecture.current;
    const debut = Date.now();
    derniereLecture.current = debut;
    enVol.current += 1;
    const vivant = () => actif() && jetonCourant.current === token && n === seqLecture.current;
    try {
      const d = await publicClientPortal.get(token);
      if (!vivant()) return;
      // Réponse inattendue (page HTML d'un proxy…) : une erreur, jamais « aucune commande ».
      if (!d || typeof d !== 'object' || !Array.isArray(d.commandes)) throw new Error('Réponse inattendue du serveur');
      setData(d);
      affichee.current = true;
      setEtat('ok');
      setEchecActu(null);
      setLu(new Date());
      derniereReussite.current = debut;
      pauseJusqua.current = 0;   // le serveur répond de nouveau : la lecture automatique reprend toutes les 2 min
      setAutoEnPause(false);
    } catch (e) {
      const s = e?.response?.status;
      // Trop de demandes (60 / 10 min par adresse IP, tous les onglets d'un bureau ensemble) : plus de lecture automatique
      // pendant 10 min, la fenêtre du serveur — même si cette réponse ne s'affiche plus, sauf si elle vient d'un AUTRE lien
      // ou si une lecture partie APRÈS celle-ci a déjà réussi (le serveur répond de nouveau) ; ↻ reste possible.
      if (s === 429 && jetonCourant.current === token && derniereReussite.current < debut) { pauseJusqua.current = Date.now() + 10 * 60_000; if (actif()) setAutoEnPause(true); }
      if (!vivant()) return;
      // Jeton inconnu / coupé : le même écran (pas d'énumération), même si une liste était affichée.
      if (s === 404 || s === 400 || s === 410) { affichee.current = false; setEtat('invalid'); setData(null); return; }
      // Lecture automatique ratée (réseau) : en silence tant que la liste affichée a moins de 5 min (l'heure affichée le
      // dit) ; au-delà, sans liste, ou trop de demandes : le message habituel.
      if (auto && s !== 429 && affichee.current && Date.now() - derniereReussite.current < 5 * 60_000) return;
      // Trop de demandes / réseau : une liste déjà affichée reste, avec un message.
      if (affichee.current) setEchecActu(s === 429 ? 'tropDeDemandes' : 'erreur');
      else setEtat(s === 429 ? 'trop' : 'erreur');
    } finally { enVol.current -= 1; }
  }, [token]);

  // Nouveau lien (autre jeton dans la même page) : rien de l'ancien ne reste à l'écran.
  useEffect(() => {
    let actif = true;
    affichee.current = false;
    derniereReussite.current = 0;
    pauseJusqua.current = 0;
    setAutoEnPause(false);
    setData(null);
    setEtat('loading');
    setEchecActu(null);
    setOuvertes(new Set());
    setFiltre('toutes');
    lire(() => actif);
    return () => { actif = false; };
  }, [lire]);

  // Jamais indexée ; langue et titre de la page suivent le bouton.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    const lang = document.documentElement.lang;
    const titre = document.title;
    return () => { meta.remove(); document.documentElement.lang = lang; document.title = titre; };
  }, []);
  useEffect(() => {
    document.documentElement.lang = langue;
    const nom = data && data.client && data.client.name;
    document.title = `${nom ? `${nom} — ` : ''}${T.titre}`;
  }, [langue, data, T.titre]);

  // Lecture automatique 2 min après la précédente (réussie ou non, automatique ou ↻) : jamais sur un onglet caché (reprise au
  // retour sur l'onglet), jamais après un lien invalide.
  const invalide = etat === 'invalid';
  useEffect(() => {
    if (invalide) return undefined;
    let actif = true;
    let t = null;
    const tic = () => {
      clearTimeout(t);
      if (document.visibilityState !== 'visible') return;
      if (enVol.current) { t = setTimeout(tic, 5_000); return; }   // une lecture (↻) est en route : jamais deux à la fois
      const reste = Math.max(derniereLecture.current + AUTO_MS, pauseJusqua.current) - Date.now();
      if (reste <= 5_000) { setAutoEnPause(false); lire(() => actif, { auto: true }); t = setTimeout(tic, AUTO_MS); } else t = setTimeout(tic, reste);
    };
    tic();
    document.addEventListener('visibilitychange', tic);
    return () => { actif = false; clearTimeout(t); document.removeEventListener('visibilitychange', tic); };
  }, [lire, invalide]);

  const actualiser = async () => {
    if (rafraichit) return;   // ↻ = toujours une lecture, avec son résultat ou son erreur (la plus récente s'affiche)
    setRafraichit(true);
    await lire();
    setRafraichit(false);
  };
  const basculer = (ref) => setOuvertes((s) => { const n = new Set(s); if (n.has(ref)) n.delete(ref); else n.add(ref); return n; });

  const entete = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <div style={brand}>Brain E-Log</div>
      <button type="button" onClick={() => setLangue((l) => (l === 'en' ? 'fr' : 'en'))} style={btnLangue}
        lang={langue === 'en' ? 'fr' : 'en'} aria-label={langue === 'en' ? 'Afficher en français' : 'Show in English'}>
        {T.langue}
      </button>
    </div>
  );

  if (etat === 'loading') return <div style={wrap}>{entete}<p style={{ color: '#5b6b73' }}>{T.chargement}</p></div>;
  if (etat !== 'ok') {
    // Lien inconnu / coupé : dans les DEUX langues (la page ne sait pas qui l'ouvre).
    const msg = etat === 'invalid'
      ? [TEXTES.en.invalide, TEXTES.fr.invalide]
      : etat === 'trop' ? [TEXTES.en.tropDeDemandes, TEXTES.fr.tropDeDemandes] : [TEXTES.en.erreur, TEXTES.fr.erreur];
    return (
      <div style={wrap}>
        {entete}
        <div role="alert" style={{ marginTop: 16, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, fontSize: 17 }}>
          <div lang="en">{msg[0]}</div>
          <div lang="fr" style={{ marginTop: 6, color: '#5b6b73' }}>{msg[1]}</div>
        </div>
        {etat !== 'invalid' && <button type="button" onClick={actualiser} style={{ ...btnFiltre(false), marginTop: 12 }}>{T.actualiser}</button>}
      </div>
    );
  }

  const commandes = data.commandes;
  const n = compteurs(commandes);
  const lignes = filtrerCommandes(commandes, filtre);
  const rang = new Map(commandes.map((c, i) => [c, i]));   // n° stable de la ligne (id du détail)
  const nomClient = data.client && data.client.name;

  return (
    <div style={wrap}>
      {entete}
      <div style={{ margin: '10px 0 14px' }}>
        <h1 style={{ fontSize: 'clamp(1.25rem, 3.4vw, 1.7rem)', margin: 0, lineHeight: 1.2, overflowWrap: 'anywhere' }}>
          {nomClient ? `${nomClient} — ${T.titre}` : T.titre}
        </h1>
        <div style={{ color: '#5b6b73', marginTop: 4, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <span>{T.sousTitre}</span>
          {lu && <span>{`· ${T.misAJour} ${lu.toLocaleTimeString(langue === 'fr' ? 'fr-BE' : 'en-GB', { timeZone: 'Europe/Brussels', hour: '2-digit', minute: '2-digit' })} (${autoEnPause ? T.autoPause : T.auto})`}</span>}
          <button type="button" onClick={actualiser} disabled={rafraichit} style={{ ...btnFiltre(false), minHeight: 36, padding: '4px 12px' }}>
            {rafraichit ? '…' : `↻ ${T.actualiser}`}
          </button>
        </div>
        {echecActu && <div role="alert" style={{ marginTop: 8, color: '#a6342b', fontWeight: 600 }}>{T[echecActu]}</div>}
      </div>

      <div role="group" aria-label={T.colEtat} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        {FILTRES.map((f) => (
          <button key={f} type="button" aria-pressed={filtre === f} onClick={() => setFiltre(f)} style={btnFiltre(filtre === f)}>
            {`${T.filtres[f]} (${n[f]})`}
          </button>
        ))}
      </div>

      {!commandes.length ? (
        <p style={{ color: '#5b6b73' }}>{T.aucune}</p>
      ) : !lignes.length ? (
        <p style={{ color: '#5b6b73' }}>{T.aucuneFiltre}</p>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 12, background: '#fff' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 15, fontVariantNumeric: 'tabular-nums' }}>
            <thead>
              <tr>
                {[T.colRef, T.colPrete, T.colUnites, T.colEtat, T.colChargee, T.colReste, ''].map((h, i) => <th key={i} style={th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {lignes.map((c) => {
                const e = etatCommande(c);
                // Clé stable d'un filtre à l'autre (et d'une actualisation à l'autre) : réf. + date « prête ».
                const cle = `${c.ref}|${c.preteLe || ''}`;
                const i = rang.get(c);
                const ouverte = ouvertes.has(cle);
                const reste = resteNombre(c.resteAQuai);
                const photos = lienSur(c.photos);
                const idDetail = `detail-${i}`;
                return (
                  <Fragment key={cle}>
                    <tr style={{ borderTop: '1px solid #e2e8f0' }}>
                      <td style={{ ...td, fontFamily: 'Consolas, "JetBrains Mono", monospace', fontWeight: 700, overflowWrap: 'anywhere' }}>{c.ref}</td>
                      <td style={td}>{dateHeure(c.preteLe, langue)}</td>
                      <td style={td}>{texteUnitesCommande(c, langue)}</td>
                      <td style={td}><span style={pastille(e)}>{T.etats[e]}</span></td>
                      <td style={td}>{e === 'CHARGEE' ? dateHeure(c.chargeeLe, langue) : '—'}</td>
                      <td style={td}>{reste ? T.unite(reste) : '—'}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        <button type="button" aria-expanded={ouverte} aria-controls={idDetail} onClick={() => basculer(cle)} style={btnDetail}>
                          {ouverte ? `▾ ${T.masquer}` : `▸ ${T.detail}`}
                        </button>
                      </td>
                    </tr>
                    {ouverte && (
                      <tr id={idDetail}>
                        <td colSpan={7} style={{ ...td, background: '#f8fafc', paddingTop: 4 }}>
                          {Array.isArray(c.detail) && c.detail.length > 0 ? (
                            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 14 }}>
                              <thead>
                                <tr>{[T.colNom, T.colSscc, T.colDims, T.colPoids, T.colCharge].map((h) => <th key={h} style={{ ...th, background: 'transparent' }}>{h}</th>)}</tr>
                              </thead>
                              <tbody>
                                {c.detail.map((u, k) => (
                                  <tr key={k} style={{ borderTop: '1px solid #e2e8f0' }}>
                                    <td style={td}>{nomDetail(u, langue)}</td>
                                    <td style={{ ...td, fontFamily: 'Consolas, "JetBrains Mono", monospace', overflowWrap: 'anywhere' }}>{u.sscc ? abregeSscc(u.sscc) : '—'}</td>
                                    <td style={td}>{texteDims(u.dims, langue)}</td>
                                    <td style={td}>{textePoids(u.poids, langue)}</td>
                                    <td style={td}>
                                      <span style={pastille(uniteChargee(u) ? 'CHARGEE' : 'QUAI')}>
                                        {uniteChargee(u) ? (dateChargeUnite(u) ? `${T.charge} · ${dateHeure(dateChargeUnite(u), langue)}` : T.charge) : T.aQuai}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : <span style={{ color: '#5b6b73' }}>—</span>}
                          {photos && (
                            <a href={photos} target="_blank" rel="noopener noreferrer" style={lienPhotos}>📷 {T.photos}</a>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
            {/* 27-10-2026 : total des commandes affichées (suit le filtre) */}
            {(() => {
              const t = totaux(lignes);
              return (
                <tfoot>
                  <tr style={{ borderTop: '2px solid #cbd5e1', background: '#f8fafc', fontWeight: 700 }}>
                    <td style={td}>{`${T.total} · ${T.commandes(t.commandes)}`}</td>
                    <td style={td} />
                    <td style={td}>{texteUnitesCommande(t, langue)}</td>
                    <td style={td} colSpan={2} />
                    <td style={td}>{t.reste ? T.unite(t.reste) : '—'}</td>
                    <td style={td} />
                  </tr>
                </tfoot>
              );
            })()}
          </table>
        </div>
      )}

      <p style={{ color: '#5b6b73', fontSize: 13, marginTop: 24 }}>
        Brain E-Log · Boulevard de la Technicité 1A, 7110 Houdeng-Goegnies, Belgium
      </p>
    </div>
  );
}

const th = { textAlign: 'left', padding: '10px 12px', fontSize: 12, color: '#5b6b73', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', background: '#f8fafc', whiteSpace: 'nowrap' };
const td = { textAlign: 'left', padding: '10px 12px', verticalAlign: 'top' };
const COULEURS = { QUAI: ['#fbecd3', '#965600'], PARTIELLE: ['#dbeafe', '#1e40af'], CHARGEE: ['#dcefdc', '#2a7034'] };
const pastille = (e) => ({ display: 'inline-block', borderRadius: 999, padding: '2px 10px', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', background: COULEURS[e][0], color: COULEURS[e][1] });
const btnFiltre = (actif) => ({
  minHeight: 44, padding: '8px 14px', fontSize: 15, fontWeight: 700, borderRadius: 999, cursor: 'pointer',
  border: `1px solid ${actif ? '#0f6e74' : '#cfd8dc'}`, background: actif ? '#0f6e74' : '#fff', color: actif ? '#fff' : '#17232a',
});
const btnLangue = { minHeight: 40, padding: '6px 14px', fontSize: 14, fontWeight: 700, borderRadius: 8, border: '1px solid #cfd8dc', background: '#fff', color: '#0f6e74', cursor: 'pointer' };
const btnDetail = { minHeight: 40, padding: '6px 10px', fontSize: 14, fontWeight: 600, borderRadius: 8, border: '1px solid #cfd8dc', background: '#fff', color: '#17232a', cursor: 'pointer' };
const lienPhotos = { display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10, minHeight: 44, padding: '8px 14px', borderRadius: 8, background: '#0f6e74', color: '#fff', fontWeight: 700, textDecoration: 'none' };
